import React, { useState, useEffect } from 'react';
import { X, Send, Phone, Mail, User, ArrowRight, ExternalLink, Search, LayoutGrid, Zap, Code, Smartphone, Brain, MessageSquare, Globe, Tag } from 'lucide-react';
import Swal from 'sweetalert2';
import CMarket from '../assets/Capitaline.png';
import Jupitor from '../assets/Jupitor.png';
import PowerBI from '../assets/PowerBI.png';
import MobileApp from '../assets/MobileApp.png';
import Jenkins from '../assets/Jenkins.png';

import RoyalThread1 from '../assets/RoyalThread_1.png';
import RoyalThread2 from '../assets/RoyalThread_2.png';
import RoyalThreadImg from '../assets/RoyalThread.png';

import SEO from '../components/SEO';
import { apiFetch } from '../utils/api';

interface PortfolioItem {
  id: string;
  title: string;
  category: string;
  description: string;
  image_url?: string;
  video_url?: string;
  demo_url?: string;
  created_at?: string;
  updated_at?: string;
}

const isVideoUrl = (url?: string): boolean => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.startsWith('data:video/') || lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov') || lower.endsWith('.ogg') || lower.includes('/video/');
};

const defaultProjects: PortfolioItem[] = [
  {
    id: "14",
    title: "devNectar AI Chatbot & Virtual Assistant",
    category: "Chatbot",
    description: "Developed an advanced AI-powered chatbot and virtual assistant integrated into the devNectar platform. Built using React, Node.js, Express, OpenRouter/Mistral AI, and Web Speech API, the assistant features real-time natural language query answering, document key-phrase extraction, voice input/output synthesis, and dynamic website navigation. It automatically matches user inquiries against a curated knowledge base and seamlessly escalates complex queries via AI search, improving user engagement and support responsiveness.",
    image_url: "/images/portfolio/Chatbot.png",
    demo_url: "/chat"
  },
  {
    id: "15",
    title: "devNectar WhatsApp Business Chatbot",
    category: "Chatbot",
    description: "Developed an automated WhatsApp AI chatbot for enterprise customer support and sales engagement. Built using WhatsApp Business API, Node.js, and Express, this bot handles automated client inquiries, quote requests, appointment scheduling, and automated instant notifications. It integrates directly with CRM databases to streamline client communication, reduce response times by 80%, and provide 24/7 automated support over WhatsApp.",
    image_url: "/images/portfolio/WhatsAppChatbot.png",
    demo_url: "https://api.whatsapp.com/send/?phone=917387186843&text&type=phone_number&app_absent=0"
  },
  {
    id: "16",
    title: "Enterprise AI Customer Support Bot",
    category: "Chatbot",
    description: "Engineered a multi-channel AI customer support bot for enterprise SaaS platforms. The system incorporates natural language understanding (NLU), automated ticketing routing, semantic FAQ resolution, and live agent handoff capabilities. Built with React, Python/FastAPI, and OpenAI GPT-4, it processes thousands of customer support tickets daily while maintaining a 94% first-contact resolution rate.",
    image_url: "/images/portfolio/KonkanMitraBot.png",
    demo_url: "https://stay-in-konkan.vercel.app/"
  },
  {
    id: "17",
    title: "E-Commerce AI Sales & Order Assistant",
    category: "Chatbot",
    description: "Created an interactive e-commerce shopping and recommendation AI chatbot. Deployed across web and mobile storefronts, the bot assists shoppers with real-time product recommendations, stock availability checks, order tracking, and discount code redemption. Features personalized conversation flows, multi-language support, and integration with Shopify and custom React e-commerce backends.",
    image_url: "/images/portfolio/HealthBot.png",
    demo_url: "https://demo.devnectar.in/"
  },
  {
    id: "6",
    title: "AI-Powered Document Processing System",
    category: "Power Platform",
    description: "Developed an intelligent document processing solution using Microsoft Power Apps, Power Automate, SharePoint, and Microsoft Azure. The system allows users to upload documents through a Power Apps interface, after which the files are securely stored in SharePoint. Azure AI extracts and analyzes the document content, and Power Automate transforms the extracted data into a structured JSON format. The processed data is then automatically saved and made available for further business workflows, reporting, and integration with other enterprise applications. This solution significantly reduces manual data entry, improves accuracy, and streamlines document management.",
    image_url: "/images/portfolio/DocumentProcessing.png"
  },
  {
    id: "13",
    title: "HR Powercraft – HR Management System",
    category: "Power Platform",
    description: "Developed a comprehensive HR Management System using Microsoft Power Apps, Power Automate, and SharePoint to streamline end-to-end employee and recruitment processes. The system manages Home dashboard, Job Applications, Interviews, Onboarding, Trainers, Employees, Attendance, Timesheets, Courses, and Leave Requests from a centralized platform. HR teams can track candidates through different recruitment stages, manage onboarding activities, and maintain employee records efficiently. Power Automate handles automated workflows, notifications, approvals, and process updates, while SharePoint securely stores and manages organizational data. The solution reduces manual HR operations, improves data accuracy, and provides a user-friendly digital experience for managing the complete employee lifecycle.",
    image_url: "/images/portfolio/HRPowercraft.png"
  },
  {
    id: "7",
    title: "Peedhee – Family Generation Management App",
    category: "React Native & Flutter",
    description: "Developed Peedhee, a Flutter-based mobile application designed to digitally preserve and manage family lineage across multiple generations. The app enables users to create and maintain detailed family trees by recording relationships such as grandparents, parents, children, and descendants. It provides an intuitive interface to visualize the complete family hierarchy, update member information, and keep historical family records organized. The application ensures easy navigation through generations, making it simple to trace ancestry and maintain an accurate digital family history.",
    image_url: "/images/portfolio/Peedhee.png"
  },
  {
    id: "19",
    title: "Sadguru Tyres – Automotive Tyre Services & Quotation Booking Platform",
    category: "React",
    description: "Developed Sadguru Tyres, a modern automotive tyre marketplace, service appointment booking, and quotation platform using React.js, Tailwind CSS, Node.js, Express, and Supabase. The application allows vehicle owners to explore top tyre brands, check specifications and pricing, schedule service appointments (wheel alignment, balancing, tyre fitting, rotation), and request instant quotations online. It includes an integrated administrative dashboard for managing service bookings, customer quotation requests, inventory, dealer network listings, and appointment schedules.",
    image_url: "/images/portfolio/SadguruTyres.png",
    demo_url: "https://sadguru-tyresdemo.vercel.app/"
  },
  {
    id: "18",
    title: "House of Urvaah – Luxury Ladies Wear & Garments E-Commerce",
    category: "React",
    description: "Developed House of Urvaah, an exquisite luxury ladies wear and designer garments e-commerce platform using React.js, Tailwind CSS, Node.js, Express, and Supabase. The platform showcases premium fashion collections, architectural tailoring, silk dresses, and artisanal printed ensembles. It features an immersive high-fashion visual storefront, interactive product showcase with size and color variants, wishlist and bag management, secure checkout, order tracking, and an admin management suite for orders, inventory, and customer relationships.",
    image_url: "/images/portfolio/HouseOfUrvaah.png",
    demo_url: "https://house-of-urvaah-fe.vercel.app/"
  },
  {
    id: "8",
    title: "HomVed – Ayurvedic E-Commerce Platform",
    category: "React",
    description: "Developed HomVed, a modern Ayurvedic e-commerce platform using React.js, Node.js, Express.js, Supabase, and Vercel. The platform enables customers to browse and purchase a wide range of Ayurvedic products through a fast, responsive, and user-friendly interface. It includes secure user authentication, product management, shopping cart, order processing, and real-time database operations powered by Supabase. The application is optimized for performance and deployed on Vercel, providing a seamless shopping experience across desktop and mobile devices.",
    image_url: "/images/portfolio/HomVed.png",
    demo_url: "https://demo.devnectar.in/"
  },
  {
    id: "9",
    title: "Royal Thread – Wholesale Sewing Thread Marketplace",
    category: "React Native & Flutter",
    description: "Developed Royal Thread, a Flutter-based mobile application for the wholesale distribution of sewing threads used in garment and apparel manufacturing. The app allows retailers, distributors, and manufacturers to browse thread products, compare specifications, and place bulk orders through a simple and user-friendly interface. It features secure user authentication, product catalog management, order tracking, and an optimized mobile experience. The application simplifies the wholesale ordering process, improves inventory accessibility, and enables businesses to efficiently purchase high-quality sewing threads for clothing production.",
    image_url: "/images/portfolio/RoyalThread.png"
  },
  {
    id: "10",
    title: "Elite Garden – WordPress Business Website",
    category: "WordPress",
    description: "Developed Elite Garden, a professional business website using WordPress to showcase landscaping and garden-related services. The website features a modern, responsive design with service pages, project galleries, contact forms, and easy content management through the WordPress CMS. It is optimized for performance, mobile devices, and search engines, enabling the business to effectively present its services and engage with potential customers. The platform provides a seamless browsing experience while allowing administrators to update content without technical expertise.",
    image_url: "/images/portfolio/EliteGarden.png"
  },
  {
    id: "11",
    title: "Muscle Therapy by Tom – WordPress Healthcare Website",
    category: "WordPress",
    description: "Developed Muscle Therapy by Tom, a professional healthcare website using WordPress to promote massage therapy and muscle rehabilitation services. The website features a clean, responsive design with service information, therapist profile, appointment inquiry forms, testimonials, and contact details. Built with an SEO-friendly structure and optimized performance, the platform provides visitors with an intuitive experience while enabling the administrator to easily manage content through the WordPress CMS.",
    image_url: "/images/portfolio/MuscleTherapy.png"
  },
  {
    id: "12",
    title: "Stay in Konkan – Accommodation Booking Platform",
    category: "React",
    description: "Developed Stay in Konkan, a full-stack accommodation booking platform using React.js, Node.js, Express.js, Supabase, and Vercel. The platform enables travelers to discover, explore, and book stays across the Konkan region through a modern and responsive interface. It includes secure user authentication, property listings, advanced search and filtering, online booking management, and real-time database operations powered by Supabase. Deployed on Vercel, the application delivers a fast, reliable, and seamless booking experience for both desktop and mobile users.",
    image_url: "/images/portfolio/StayInKonkan.png",
    demo_url: "https://stay-in-konkan.vercel.app/"
  },
  {
    id: "1",
    title: "Capitaline Database",
    category: "Database / Web Application",
    description: "Capital Market is largest and most comprehensive database on Indian companies, covering over 50,000 public and private companies. It provides detailed financials, ownership structures, industry classifications, and more. The database is widely used by researchers, analysts, and investors for market analysis and decision-making. We developed website and screener for them using Angular, Node.js and hosted on AWS. Screener is most complex search engine which can filter companies based on 100+ parameters to build report from historical data of past 5 years and more.",
    image_url: CMarket
  },
  {
    id: "2",
    title: "Jupiter Tatravagonka",
    category: "SharePoint / Intranet",
    description: "Jupiter Tatravagonka Railwheel Factory Pvt. Ltd., a joint venture between Jupiter Wagons (JWL) and the European rail infrastructure company Tatravagonka a.s. JWL, a major Indian railway equipment manufacturer. We setup sharepoint 2019 on-prem environment for them and develop intranet site for HR department to publish policies, news and other HR related activities.",
    image_url: Jupitor
  },
  {
    id: "3",
    title: "Power Platform",
    category: "Power Apps & Analytics",
    description: "Developed Power Platform solutions including Power Apps, Power Automate flows, and Power BI reports for various clients. Solutions ranged from simple data entry apps to complex workflow automations and interactive dashboards. Integrated with SharePoint, SQL Server, and third-party APIs to streamline business processes and enhance data visibility.",
    image_url: PowerBI
  },
  {
    id: "4",
    title: "Mobile Apps Development",
    category: "React Native & Flutter",
    description: "Mobile applications developed for both iOS and Android platforms using React Native and Flutter. Apps included features such as user authentication, push notifications, in-app purchases, and integration with RESTful APIs. Focused on delivering intuitive user interfaces and seamless performance across devices. Developed for one of financial client for their customer to track their Capital Market Portfolio.",
    image_url: MobileApp
  },
  {
    id: "5",
    title: "Jenkins",
    category: "DevOps & CI/CD",
    description: "Implemented Jenkins for continuous integration and continuous deployment (CI/CD) pipelines. Automated build, test, and deployment processes for various applications, ensuring faster delivery and higher quality. Integrated with version control systems like Git, and used plugins for code analysis, notifications, and artifact management.",
    image_url: Jenkins
  }
];

const getImageForProject = (title: string, imageUrl?: string): string => {
  if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
    return imageUrl;
  }
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('sadguru') || lowerTitle.includes('tyre')) return '/images/portfolio/SadguruTyres.png';
  if (lowerTitle.includes('urvaah')) return '/images/portfolio/HouseOfUrvaah.png';
  if (lowerTitle.includes('e-commerce') || lowerTitle.includes('healthbot')) return '/images/portfolio/HealthBot.png';
  if (lowerTitle.includes('enterprise') || lowerTitle.includes('mitra')) return '/images/portfolio/KonkanMitraBot.png';
  if (lowerTitle.includes('whatsapp')) return '/images/portfolio/WhatsAppChatbot.png';
  if (lowerTitle.includes('chatbot') || lowerTitle.includes('assistant')) return '/images/portfolio/Chatbot.png';
  if (lowerTitle.includes('document')) return '/images/portfolio/DocumentProcessing.png';
  if (lowerTitle.includes('hr powercraft')) return '/images/portfolio/HRPowercraft.png';
  if (lowerTitle.includes('peedhee')) return '/images/portfolio/Peedhee.png';
  if (lowerTitle.includes('homved')) return '/images/portfolio/HomVed.png';
  if (lowerTitle.includes('royal')) return RoyalThreadImg;
  if (lowerTitle.includes('elite garden')) return '/images/portfolio/EliteGarden.png';
  if (lowerTitle.includes('muscle therapy')) return '/images/portfolio/MuscleTherapy.png';
  if (lowerTitle.includes('stay in konkan')) return '/images/portfolio/StayInKonkan.png';
  if (lowerTitle.includes('capitaline')) return CMarket;
  if (lowerTitle.includes('jupiter')) return Jupitor;
  if (lowerTitle.includes('power')) return PowerBI;
  if (lowerTitle.includes('mobile')) return MobileApp;
  if (lowerTitle.includes('jenkins')) return Jenkins;
  return RoyalThreadImg;
};

const getCategoryIcon = (category: string, active: boolean = false) => {
  const cat = category.toLowerCase();
  if (cat === 'all') {
    return <LayoutGrid className={`w-4 h-4 shrink-0 ${active ? 'text-slate-200' : 'text-slate-500'}`} />;
  }
  if (cat.includes('power')) {
    return <Zap className={`w-4 h-4 shrink-0 ${active ? 'text-amber-400 fill-amber-400' : 'text-amber-500'}`} />;
  }
  if (cat.includes('react native')) {
    return <Code className={`w-4 h-4 shrink-0 ${active ? 'text-cyan-300' : 'text-cyan-500'}`} />;
  }
  if (cat.includes('react')) {
    return <Code className={`w-4 h-4 shrink-0 ${active ? 'text-cyan-300' : 'text-cyan-500'}`} />;
  }
  if (cat.includes('flutter')) {
    return <Smartphone className={`w-4 h-4 shrink-0 ${active ? 'text-sky-300' : 'text-sky-500'}`} />;
  }
  if (cat.includes('ai') || cat.includes('ml')) {
    return <Brain className={`w-4 h-4 shrink-0 ${active ? 'text-purple-300' : 'text-purple-500'}`} />;
  }
  if (cat.includes('bot') || cat.includes('chatbot')) {
    return <MessageSquare className={`w-4 h-4 shrink-0 ${active ? 'text-emerald-300' : 'text-emerald-500'}`} />;
  }
  if (cat.includes('wordpress')) {
    return <Globe className={`w-4 h-4 shrink-0 ${active ? 'text-blue-300' : 'text-blue-600'}`} />;
  }
  return <Tag className={`w-4 h-4 shrink-0 ${active ? 'text-slate-300' : 'text-slate-500'}`} />;
};

const Portfolio: React.FC = () => {
  const [projects, setProjects] = useState<PortfolioItem[]>(defaultProjects);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    requirement: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        const res = await apiFetch('/portfolio');
        if (res.ok) {
          const contentType = res.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await res.json();
            const items = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : null);
            if (items && items.length > 0) {
              const mergedItems = items.map((item: PortfolioItem) => {
                const defaultMatch = defaultProjects.find(
                  (dp) => String(dp.id) === String(item.id) || dp.title.toLowerCase().trim() === (item.title || '').toLowerCase().trim()
                );
                const resolvedDemoUrl = (item.demo_url && typeof item.demo_url === 'string' && item.demo_url.trim() !== '')
                  ? item.demo_url
                  : (defaultMatch?.demo_url || '');
                return {
                  ...item,
                  demo_url: resolvedDemoUrl
                };
              });
              const backendTitles = new Set(items.map((item: PortfolioItem) => (item.title || '').toLowerCase().trim()));
              const missingDefaults = defaultProjects.filter((dp) => !backendTitles.has(dp.title.toLowerCase().trim()));
              setProjects([...mergedItems, ...missingDefaults]);
            }
          }
        }
      } catch (err) {
        console.warn('Using default portfolio projects:', err);
      }
    };
    loadPortfolio();
  }, []);

  useEffect(() => {
    if (showDemoModal) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('demo-modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('demo-modal-open');
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('demo-modal-open');
    };
  }, [showDemoModal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    Swal.fire({
      icon: 'success',
      title: 'Request Received!',
      text: `Thank you ${formData.name}, your demo request has been sent to our team, we will contact you soon.`,
      confirmButtonText: 'Great!',
      confirmButtonColor: '#3b82f6',
      timer: 5000,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl px-10 py-3 font-semibold'
      }
    });

    setShowDemoModal(false);
    setFormData({ name: '', email: '', mobile: '', requirement: '' });
  };

  const categories = ['All', 'React', 'Power Platform', 'Flutter', 'AI/ML', 'Chatbot', 'WordPress', 'Other'];

  const filteredProjects = projects.filter(project => {
    const projectCat = (project.category || '').toLowerCase();
    const projectTitle = (project.title || '').toLowerCase();
    const projectDesc = (project.description || '').toLowerCase();

    const matchesReact = (projectCat.includes('react') || projectTitle.includes('react')) && !projectCat.includes('native') && !projectTitle.includes('native');
    const matchesPowerPlatform = projectCat.includes('power') || projectTitle.includes('power');
    const matchesFlutter = projectCat.includes('flutter') || projectTitle.includes('flutter');
    const matchesChatbot = projectCat.includes('bot') || projectCat.includes('chat') || projectCat.includes('assistant') || projectTitle.includes('bot') || projectTitle.includes('chat') || projectTitle.includes('assistant') || projectDesc.includes('chatbot') || projectDesc.includes('chat bot') || projectDesc.includes('ai assistant');
    const matchesAIML = (projectCat.includes('ai') || projectCat.includes('ml') || projectTitle.includes('ai') || projectTitle.includes('ml') || projectTitle.includes('ai-powered')) && !matchesChatbot;
    const matchesWordPress = projectCat.includes('wordpress') || projectTitle.includes('wordpress') || projectDesc.includes('wordpress');

    const matchesAnySpecificCategory = matchesReact || matchesPowerPlatform || matchesFlutter || matchesAIML || matchesChatbot || matchesWordPress;

    // 1. Category Filter
    if (selectedCategory !== 'All') {
      const cat = selectedCategory.toLowerCase();

      if (cat === 'react') {
        if (!matchesReact) return false;
      } else if (cat === 'power platform') {
        if (!matchesPowerPlatform) return false;
      } else if (cat === 'flutter') {
        if (!matchesFlutter) return false;
      } else if (cat === 'ai/ml' || cat === 'ai & ml') {
        if (!matchesAIML && !projectCat.includes('ai')) return false;
      } else if (cat === 'chatbot') {
        if (!matchesChatbot && !projectCat.includes('chat') && !projectCat.includes('bot')) return false;
      } else if (cat === 'wordpress') {
        if (!matchesWordPress) return false;
      } else if (cat === 'other') {
        if (matchesAnySpecificCategory) return false;
      } else {
        if (!projectCat.includes(cat) && !projectTitle.includes(cat) && !projectDesc.includes(cat)) return false;
      }
    }

    // 2. Search Query Filter
    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase().trim();
      const matchesTitle = projectTitle.includes(q);
      const matchesCategory = projectCat.includes(q);
      const matchesDesc = projectDesc.includes(q);
      if (!matchesTitle && !matchesCategory && !matchesDesc) return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen">
      <SEO
        title="Portfolio"
        description="Key deliverables and success stories from our recent projects. Explore our work in SharePoint, Mobile Apps, and Cloud Solutions."
        keywords="Project Portfolio, Case Studies, Software Success Stories, SharePoint Solutions, React Native Apps"
      />
      
      {/* Hero Section */}
      <section data-id="contact-hero" className="relative bg-gradient-to-br from-blue-900 via-blue-900 to-indigo-900 text-white py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full -ml-48 -mb-48 blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="absolute top-1/2 -translate-y-1/2 right-0 sm:right-4 lg:right-6 z-10">
            <button
              onClick={() => setShowDemoModal(true)}
              className="group relative px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-full shadow-lg hover:shadow-blue-500/50 hover:-translate-y-1 active:translate-y-0 transition-all duration-300 flex items-center gap-2"
            >
              Request Demo
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            </button>
          </div>

          <div className="text-center">
            <h1 data-id="contact-hero-title" className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 animate-gradient-x">Portfolio</span>
            </h1>
            <p data-id="contact-hero-description" className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Key deliverables and success stories from our recent projects.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Category Filter Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-3 mb-4">
        <div className="bg-white rounded-3xl p-3.5 md:p-4 shadow-sm border border-gray-200/80 flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch lg:items-center justify-between">
          {/* Search Input (Expands to All button) */}
          <div className="relative flex-1 min-w-[200px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="w-4 h-4 text-gray-400" />
            </span>
            <input
              type="text"
              placeholder="Search projects..."
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200/80 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-xs sm:text-sm text-gray-900"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Category Tabs */}
          <div className="flex-shrink-0 flex flex-nowrap gap-1.5 sm:gap-2 items-center overflow-x-auto no-scrollbar py-0.5">
            {categories.map((category) => {
              const active = selectedCategory === category;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 sm:px-3.5 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-full whitespace-nowrap transition-all duration-200 flex-shrink-0 flex items-center gap-1.5 ${
                    active
                      ? 'bg-[#0B132A] text-white shadow-md'
                      : 'bg-slate-100 hover:bg-slate-200/80 text-slate-700'
                  }`}
                >
                  {getCategoryIcon(category, active)}
                  <span>{category}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Demo Request Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-white/40 backdrop-blur-md animate-fade-in"
            onClick={() => setShowDemoModal(false)}
          ></div>
          <div className="relative bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-form-open">
            <div className="pt-8 pb-2 px-6 text-center relative">
              <button 
                onClick={() => setShowDemoModal(false)}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
              <h2 className="text-2xl font-bold text-gray-900">Request a Demo</h2>
              <p className="text-gray-500 text-sm mt-1">Experience devNectar in action</p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    required
                    type="text"
                    placeholder="Enter your full name"
                    className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      required
                      type="email"
                      placeholder="Enter your email address"
                      className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Mobile Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      required
                      type="tel"
                      pattern="\d{10}"
                      title="Please enter exactly 10 digits"
                      placeholder="Enter your mobile number"
                      className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Demo Requirements</label>
                <textarea
                  required
                  rows={2}
                  placeholder="Please mention project name"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                  value={formData.requirement}
                  onChange={(e) => setFormData({...formData, requirement: e.target.value})}
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-blue-700 to-indigo-800 text-white font-bold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2 group btn-premium hover-pulse"
              >
                Submit Request
                <Send size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Dynamic Projects Showcase */}
      <div className="space-y-12 pt-0 pb-12">
        {filteredProjects.length > 0 ? (
          filteredProjects.map((project, index) => (
            <section
              key={project.id || index}
              className={`pb-16 ${index === 0 ? 'pt-4' : 'pt-16'} ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Image Section */}
                <div className={`flex justify-center ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative group max-w-xl w-full flex justify-center items-center">
                    {project.video_url || isVideoUrl(project.image_url) ? (
                      <video
                        src={project.video_url || project.image_url}
                        poster={project.image_url && project.image_url !== project.video_url ? getImageForProject(project.title, project.image_url) : undefined}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="max-h-[290px] sm:max-h-[330px] w-auto max-w-full rounded-2xl border-2 border-gray-300 shadow-xl bg-gray-100 object-contain p-1 group-hover:scale-102 transition-transform duration-300"
                      />
                    ) : project.title.toLowerCase().includes('royal') ? (
                      <div className="flex items-center justify-center gap-3 sm:gap-4 w-full">
                        <img
                          src={RoyalThread1}
                          alt={`${project.title} Home Screen`}
                          loading="lazy"
                          className="max-h-[270px] sm:max-h-[310px] w-auto max-w-[48%] object-contain rounded-2xl border-2 border-gray-300 shadow-xl bg-gray-100 p-1 sm:p-1.5 group-hover:scale-102 transition-transform duration-300"
                        />
                        <img
                          src={RoyalThread2}
                          alt={`${project.title} Log Visit Screen`}
                          loading="lazy"
                          className="max-h-[270px] sm:max-h-[310px] w-auto max-w-[48%] object-contain rounded-2xl border-2 border-gray-300 shadow-xl bg-gray-100 p-1 sm:p-1.5 group-hover:scale-102 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <img
                        src={getImageForProject(project.title, project.image_url)}
                        alt={project.title}
                        loading="lazy"
                        className="max-h-[290px] sm:max-h-[330px] w-auto max-w-full object-contain rounded-2xl border-2 border-gray-300 shadow-xl bg-gray-100 p-1.5 sm:p-2 group-hover:scale-102 transition-transform duration-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = CMarket;
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Content Section */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center lg:text-left">
                    {project.title}
                  </h2>
                  <p className="text-gray-600 text-base leading-relaxed mb-6">
                    {project.description}
                  </p>
                  {project.demo_url && (
                    <div className="text-center lg:text-left">
                      <a
                        href={project.demo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-bold text-sm transition-colors"
                      >
                        Visit Project Demo <ExternalLink size={16} />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
          ))
        ) : (
          <div className="text-center py-20">
            <p className="text-xl text-gray-500 font-medium">No projects found matching your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;